import { Icon as BlueIcon } from '@blueprintjs/core';
import { useContext, useEffect, useState as reactUseState } from 'react';

import SerachClearIcon from '@assets/svg/search-clear.svg';
import DownIcon from '@assets/svg/table-down.svg';
import SerachIcon from '@assets/svg/table-search.svg';
import UpIcon from '@assets/svg/table-up.svg';
import Rolling from '@assets/gif/rolling.gif';

import { useState } from '@hookstate/core';

import { useHistory, useParams } from 'react-router-dom';

import { IOnboardingFile } from '@models/Onboarding.model';

import { InfiniteData } from 'react-query';
import { useInView } from 'react-intersection-observer';

import {
  IOnboardingGroup,
  IPaginationResultGroups,
} from '@models/OnboardingGroup.model';
import { NavigationContext } from '@layout/navigation/navigation';
import { TruncatedTextTooltip } from '@components/Tooltip';
import { IdentifiedOnboardingGroup } from '@models/IdentifiedOnboardingGroup.model';
import { OnboardingFileContext } from './OnboardingFile.query';

import {
  Container,
  Content,
  Header,
  Title,
  Heading,
  Spacing,
  ColumnName,
  ColumnContainer,
  Buttons,
  Direction,
  InputContainer,
  InputSearch,
  SearchIconContainer,
  SearchButton,
  SearchClear,
  TitleBack,
  TitleContainer,
  HeaderBottom,
  HeaderBottomLeft,
  LoadMore,
  LoadingText,
  LoadingImg,
  TabsContainer,
  Tabs,
  Indicator,
  LabelText,
  Label,
  Count,
  Back,
  ItemTab,
  HelpWrapper,
  HelpButton,
  Overflow,
} from './OnboardingFile.styles';
import { Discussion } from '../Discussion/Discussion';

import { CountLoading, TableContentLoading } from './OnboardingFile.loading';

import { ErrorOnboarding } from '../ErrorOnboarding/ErrorOnboarding';
import { AccessoryDialog } from '../AccessoryDialog/AccessoryDialog';
import { EmptyOnboarding } from '../EmptyOnboarding/EmptyOnboarding';
import { ManufacturerErrorDialog } from '../ManufacturerErrorDialog/ManufacturerErrorDialog';

import { ModelErrorDialog } from '../ModelErrorDialog/ModelErrorDialog';
import { GroupRow, IdentifiedGroupedFile } from './components';
import { IntenalProductMoreInfoDialog } from '../IntenalProductMoreInfoDialog/IntenalProductMoreInfoDialog';

interface IColumnTitle {
  label: string;
  direction?: 'up' | 'down';
  onClick?: () => void;
}

const ColumnTitle = ({ label, direction, ...rest }: IColumnTitle) => {
  return (
    <ColumnContainer {...rest}>
      <ColumnName>{label}</ColumnName>
      {!!direction && (
        <Direction>
          <img src={direction === 'up' ? UpIcon : DownIcon} alt="direction" />
        </Direction>
      )}
    </ColumnContainer>
  );
};

const CountComponent = ({ count }: { count?: number }) => {
  if (count !== undefined) {
    return <Count>{count}</Count>;
  }

  return <CountLoading />;
};

interface IOnboardingFileRaw {
  groupsQuery?: InfiniteData<IPaginationResultGroups<IOnboardingGroup>>;
  identifiedGroupsQuery?: InfiniteData<
    IPaginationResultGroups<IdentifiedOnboardingGroup>
  >;
  fileData?: IOnboardingFile;
  fetchNextPage: () => void;
  isError?: boolean;
  isLoading?: boolean;
}

const OnboardingFileRaw = ({
  groupsQuery,
  identifiedGroupsQuery,
  fileData,
  fetchNextPage,
  isError,
  isLoading,
}: IOnboardingFileRaw) => {
  const openIdentificationStatusDialog = useState<boolean>(false);

  const [isOverflowed, setIsOverflowed] = reactUseState(false);

  const {
    search,
    status,
    errorDialog,
    selectedGroup,
    accessoryDialog,
    fieldErrorSelected,
    errorManufacturerDialog,
    openChat,
  } = useContext(OnboardingFileContext);

  const { isSuperAdmin } = useContext(NavigationContext);

  const params = useParams<{ id: string }>();

  const { ref, inView } = useInView();

  const hasMore = groupsQuery
    ? !groupsQuery.pages[groupsQuery.pages.length - 1].isLastPage
    : false;

  const identifiedHasMore = identifiedGroupsQuery
    ? !identifiedGroupsQuery.pages[identifiedGroupsQuery.pages.length - 1]
        .isLastPage
    : false;

  const groups = groupsQuery
    ? groupsQuery.pages
        .map(pageData => pageData.assets)
        .reduce(function reduce(prev, next) {
          return prev.concat(next);
        })
    : [];

  const identifiedGroups = identifiedGroupsQuery
    ? identifiedGroupsQuery.pages
        .map(pageData => pageData.assets)
        .reduce(function reduce(prev, next) {
          return prev.concat(next);
        })
    : [];

  useEffect(() => {
    if (inView && (hasMore || identifiedHasMore)) fetchNextPage();
  }, [inView, hasMore, identifiedHasMore]);

  const history = useHistory();

  const searchLocal = useState<string>('');

  const searchEdited = useState<boolean>(false);

  const searchState = search.get() !== searchLocal.get() || searchEdited.get();

  const clearState =
    search.get() === searchLocal.get() &&
    searchLocal.get() !== '' &&
    !searchEdited.get();

  const callSearch = () => {
    search.set(searchLocal.get());
    searchEdited.set(false);
  };

  const clearSearch = () => {
    searchLocal.set('');
    search.set('');
    searchEdited.set(false);
  };

  const selectedGroupValue = selectedGroup.value;

  return (
    <Container>
      <IntenalProductMoreInfoDialog
        open={openIdentificationStatusDialog.get()}
        onClose={() => openIdentificationStatusDialog.set(false)}
      />
      <Header>
        <HeaderBottomLeft>
          <TitleContainer>
            <TitleBack onClick={() => history.push('/onboarding')}>
              <BlueIcon icon="arrow-left" size={24} color="#8F95B2" />
            </TitleBack>

            <Title
              data-hover={isOverflowed}
              data-for={fileData?.id}
              data-tip={fileData?.originalname}
            >
              <Overflow onChange={setIsOverflowed}>
                {fileData?.originalname}
              </Overflow>
            </Title>

            {isOverflowed && <TruncatedTextTooltip id={fileData?.id} />}
          </TitleContainer>
          <HelpWrapper>
            <HelpButton
              role="button"
              onClick={() => openIdentificationStatusDialog.set(true)}
            >
              <BlueIcon icon="help" size={16} color="#8F95B2" />
              Help
            </HelpButton>
          </HelpWrapper>
        </HeaderBottomLeft>

        <TabsContainer>
          <Tabs>
            <ItemTab
              onClick={() => {
                history.push(`/onboarding/${params.id}?tab=approx_match`);
              }}
            >
              <Label>
                <LabelText isActive={status === 'approx_match'}>
                  Approx. Match
                </LabelText>

                <CountComponent
                  count={fileData?.uniqueAssets.approxMatchItems}
                />
              </Label>

              <Indicator isActive={status === 'approx_match'} />
            </ItemTab>
            <ItemTab
              onClick={() => {
                history.push(`/onboarding/${params.id}?tab=unidentified`);
              }}
            >
              <Label>
                <LabelText isActive={status === 'unidentified'}>
                  Unidentified Products
                </LabelText>

                <CountComponent
                  count={fileData?.uniqueAssets.unidentifiedItems}
                />
              </Label>

              <Indicator isActive={status === 'unidentified'} />
            </ItemTab>
            <ItemTab
              onClick={() => {
                history.push(
                  `/onboarding/${params.id}?tab=identification_requested`,
                );
              }}
            >
              <Label>
                <LabelText isActive={status === 'identification_requested'}>
                  Identification Requests
                </LabelText>

                <CountComponent count={fileData?.uniqueAssets.requestItems} />
              </Label>

              <Indicator isActive={status === 'identification_requested'} />
            </ItemTab>
            <ItemTab
              onClick={() => {
                history.push(`/onboarding/${params.id}?tab=identified`);
              }}
            >
              <Label>
                <LabelText isActive={status === 'identified'}>
                  Identified Products
                </LabelText>

                <CountComponent
                  count={fileData?.uniqueAssets.identifiedItems}
                />
              </Label>

              <Indicator isActive={status === 'identified'} />
            </ItemTab>
            <ItemTab
              onClick={() => {
                history.push(`/onboarding/${params.id}?tab=inventory`);
              }}
            >
              <Label>
                <LabelText isActive={status === 'inventory'}>
                  Internal Products
                </LabelText>

                <CountComponent count={fileData?.uniqueAssets.inventoryItems} />
              </Label>

              <Indicator isActive={status === 'inventory'} />
            </ItemTab>
          </Tabs>
          <Back />
        </TabsContainer>

        <HeaderBottom>
          <div />
          <Buttons>
            <InputContainer>
              <SearchIconContainer>
                <img src={SerachIcon} alt="search" />
              </SearchIconContainer>
              <InputSearch
                placeholder="Search"
                value={searchLocal.get()}
                onChange={e => {
                  searchLocal.set(e.target.value);
                  searchEdited.set(true);

                  searchLocal.get().length < 1 && clearSearch();
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    callSearch();
                  }
                }}
                search={searchState}
                clear={clearState}
              />
              {searchState && (
                <SearchButton onClick={callSearch}>Search</SearchButton>
              )}
              {clearState && (
                <SearchClear onClick={clearSearch}>
                  <img src={SerachClearIcon} alt="clear" />
                </SearchClear>
              )}
            </InputContainer>
            {/* <FilterContainer>
              <BlueIcon icon="filter" size={16} color="#C2CAF8" />
            </FilterContainer> */}
            {/* <MenuButton>
            <OnboardingFileMenu />
          </MenuButton> */}
          </Buttons>
        </HeaderBottom>
      </Header>

      {(groupsQuery && groups.length === 0) ||
      (identifiedGroupsQuery && identifiedGroups.length === 0) ? (
        <EmptyOnboarding
          title="There are no assets yet!"
          message="Your assets will show here once imported."
        />
      ) : (
        <Spacing>
          <Heading data-isidentified={status}>
            <ColumnTitle label="Manufacturer" />
            <ColumnTitle label="Model #" />
            <ColumnTitle label="Product/Family Name" />
            <ColumnTitle label="Quantity" />
            <div />
            {status === 'approx_match' && <div />}
          </Heading>
        </Spacing>
      )}

      {identifiedGroupsQuery && (
        <Content>
          {identifiedGroups.map((identifiedGroup, _idx) => {
            return (
              <IdentifiedGroupedFile
                key={`${identifiedGroup.matchedTo}${_idx}`}
                groups={identifiedGroup}
              />
            );
          })}

          <LoadMore ref={ref}>
            {identifiedHasMore ? (
              <LoadMore>
                <LoadingText>Loading more</LoadingText>
                <LoadingImg src={Rolling} alt="loading" />
              </LoadMore>
            ) : (
              <div />
            )}
          </LoadMore>
        </Content>
      )}

      {groupsQuery && (
        <Content>
          {groups.map(group => {
            return <GroupRow key={group.id} group={group} />;
          })}

          <LoadMore ref={ref}>
            {hasMore ? (
              <LoadMore>
                <LoadingText>Loading more</LoadingText>
                <LoadingImg src={Rolling} alt="loading" />
              </LoadMore>
            ) : (
              <div />
            )}
          </LoadMore>
        </Content>
      )}

      {isLoading && <TableContentLoading tab={status} />}

      {isError && <ErrorOnboarding message="Error on loading assets" />}

      {openChat.get() && selectedGroupValue && (
        <Discussion
          open={openChat.get()}
          onClose={() => openChat.set(false)}
          group={selectedGroupValue}
        />
      )}
      {errorDialog.get() && selectedGroupValue && isSuperAdmin && (
        <ModelErrorDialog
          open={errorDialog.get()}
          onClose={() => errorDialog.set(false)}
          group={selectedGroupValue}
          field={fieldErrorSelected.get()}
        />
      )}
      {errorManufacturerDialog.get() && selectedGroupValue && isSuperAdmin && (
        <ManufacturerErrorDialog
          open={errorManufacturerDialog.get()}
          onClose={() => errorManufacturerDialog.set(false)}
          group={selectedGroupValue}
          field={fieldErrorSelected.get()}
        />
      )}
      {accessoryDialog.get() && selectedGroupValue && isSuperAdmin && (
        <AccessoryDialog
          open={accessoryDialog.get()}
          onClose={() => accessoryDialog.set(false)}
          group={selectedGroupValue}
          tab={status}
        />
      )}
    </Container>
  );
};

export { OnboardingFileRaw };
